import { testSupabaseConnection, mapDatabaseStructure } from './test-connection'
import { testExpectedTables, testTablesViaREST, testConnectivity } from './database-inspector'

export type TestRunResult = {
  startedAt: string
  finishedAt: string
  durationMs: number
  results: Record<string, any>
  summary: {
    passed: number
    failed: number
    total: number
  }
  errors: Array<{ name: string; error: string }>
}

export async function runAllSupabaseTests(): Promise<TestRunResult> {
  const started = Date.now()
  const results: Record<string, any> = {}
  const errors: Array<{ name: string; error: string }> = []

  const run = async (name: string, fn: () => Promise<any>) => {
    try {
      const res = await fn()
      results[name] = res
      return res
    } catch (e: any) {
      const err = e instanceof Error ? e.message : String(e)
      results[name] = { success: false, error: err }
      errors.push({ name, error: err })
      return null
    }
  }

  // Execute tests in sequence
  await run('testSupabaseConnection', testSupabaseConnection)
  await run('mapDatabaseStructure', mapDatabaseStructure)
  await run('testExpectedTables', testExpectedTables)
  await run('testTablesViaREST', testTablesViaREST)
  await run('testConnectivity', testConnectivity)

  // Compute summary
  let passed = 0
  let failed = 0
  const keys = Object.keys(results)

  for (const key of keys) {
    const r = results[key]
    if (r && typeof r === 'object') {
      if ('success' in r) {
        if (r.success) passed++
        else failed++
      } else if (Array.isArray(r)) {
        // connectivity returns array, count as passed if all ok
        const allOk = r.every((t: any) => t.ok)
        if (allOk) passed++
        else failed++
      } else {
        // Fallback: if exists flag or no error assume pass
        if (!r.error) passed++
        else failed++
      }
    } else {
      failed++
    }
  }

  const finished = Date.now()
  return {
    startedAt: new Date(started).toISOString(),
    finishedAt: new Date(finished).toISOString(),
    durationMs: finished - started,
    results,
    summary: { passed, failed, total: keys.length },
    errors,
  }
}

export function formatTestRunSummary(run: TestRunResult) {
  const lines: string[] = []
  lines.push(`Test run: ${run.startedAt} → ${run.finishedAt} (${run.durationMs} ms)`) 
  lines.push(`Summary: ${run.summary.passed} passed, ${run.summary.failed} failed, ${run.summary.total} total`)
  if (run.errors.length) {
    lines.push('Errors:')
    for (const e of run.errors) lines.push(`- ${e.name}: ${e.error}`)
  }
  return lines.join('\n')
}
